// apps/web/src/pages/super/SuperRakutenEventsPage.tsx
// 楽天イベント（スーパーSALE・お買い物マラソン等）の全店共通マスタ管理
import React, { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/apiClient";

type EventType = "SS" | "MA" | "OTHER";

type RakutenEvent = {
    id: string;
    type: EventType;
    label: string;
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
};

const TYPE_LABELS: Record<EventType, string> = {
    SS: "スーパーSALE",
    MA: "お買い物マラソン",
    OTHER: "その他",
};

const emptyForm = {
    type: "SS" as EventType,
    label: "",
    startDate: "",
    endDate: "",
};

export default function SuperRakutenEventsPage() {
    const [events, setEvents] = useState<RakutenEvent[]>([]);
    const [canEdit, setCanEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiGet("/api/super/rakuten-events");
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setEvents(Array.isArray(json.events) ? json.events : []);
            setCanEdit(json.canEdit === true);
        } catch (e: any) {
            setError(e?.message ?? "読み込みに失敗しました");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
    };

    const startEdit = (ev: RakutenEvent) => {
        setEditingId(ev.id);
        setForm({
            type: ev.type,
            label: ev.label,
            startDate: ev.startDate,
            endDate: ev.endDate,
        });
    };

    const submit = async () => {
        if (!form.startDate || !form.endDate) {
            setError("開始日と終了日を入力してください");
            return;
        }
        if (form.startDate > form.endDate) {
            setError("終了日は開始日以降にしてください");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                type: form.type,
                label: form.label.trim(),
                startDate: form.startDate,
                endDate: form.endDate,
            };
            const res = editingId
                ? await apiPut(`/api/super/rakuten-events/${editingId}`, payload)
                : await apiPost("/api/super/rakuten-events", payload);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body?.error ?? `HTTP ${res.status}`);
            }
            resetForm();
            await load();
        } catch (e: any) {
            setError(e?.message ?? "保存に失敗しました");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (ev: RakutenEvent) => {
        const label = ev.label || TYPE_LABELS[ev.type];
        if (!window.confirm(`「${label}（${ev.startDate}〜${ev.endDate}）」を削除しますか？`)) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const res = await apiDelete(`/api/super/rakuten-events/${ev.id}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            if (editingId === ev.id) resetForm();
            await load();
        } catch (e: any) {
            setError(e?.message ?? "削除に失敗しました");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="h-full overflow-auto p-2">
            <div className="mb-2">
                <h1 className="text-sm font-bold">楽天イベント管理</h1>
                <p className="text-xs text-gray-600 mt-1">
                    スーパーSALE・お買い物マラソン等の期間を登録します。登録したイベントは
                    <b>全店舗</b>
                    の日次利益管理の日付欄にバッジ表示されます（表示専用・利益計算には影響しません）。
                </p>
            </div>

            {error && (
                <div className="mb-2 text-xs text-red-600 border border-red-200 bg-red-50 rounded p-2">
                    {error}
                </div>
            )}

            {canEdit && (
                <div className="mb-4 border rounded bg-white p-3">
                    <div className="text-xs font-semibold mb-2">
                        {editingId ? "イベントを編集" : "イベントを追加"}
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <label className="text-xs text-gray-600">
                            種別
                            <select
                                className="block border rounded px-2 py-1 text-xs mt-1"
                                value={form.type}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, type: e.target.value as EventType }))
                                }
                            >
                                <option value="SS">スーパーSALE</option>
                                <option value="MA">お買い物マラソン</option>
                                <option value="OTHER">その他</option>
                            </select>
                        </label>
                        <label className="text-xs text-gray-600">
                            表示名（省略時は種別名）
                            <input
                                type="text"
                                className="block border rounded px-2 py-1 text-xs mt-1 w-52"
                                placeholder={TYPE_LABELS[form.type]}
                                value={form.label}
                                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                            />
                        </label>
                        <label className="text-xs text-gray-600">
                            開始日
                            <input
                                type="date"
                                className="block border rounded px-2 py-1 text-xs mt-1"
                                value={form.startDate}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, startDate: e.target.value }))
                                }
                            />
                        </label>
                        <label className="text-xs text-gray-600">
                            終了日
                            <input
                                type="date"
                                className="block border rounded px-2 py-1 text-xs mt-1"
                                value={form.endDate}
                                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                            />
                        </label>
                        <button
                            type="button"
                            disabled={saving}
                            onClick={submit}
                            className="text-xs px-3 py-1.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                        >
                            {editingId ? "更新" : "追加"}
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                disabled={saving}
                                onClick={resetForm}
                                className="text-xs px-3 py-1.5 rounded border bg-white hover:bg-gray-50"
                            >
                                キャンセル
                            </button>
                        )}
                    </div>
                </div>
            )}

            {!canEdit && !loading && (
                <div className="mb-2 text-xs text-gray-500">
                    ※閲覧のみ（登録・編集は ecvision の管理者が行えます）
                </div>
            )}

            <div className="border rounded bg-white overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">期間</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">種別</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">表示名</th>
                            {canEdit && (
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">操作</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={canEdit ? 4 : 3} className="px-3 py-4 text-center text-gray-500">
                                    読み込み中...
                                </td>
                            </tr>
                        )}
                        {!loading && events.length === 0 && (
                            <tr>
                                <td colSpan={canEdit ? 4 : 3} className="px-3 py-4 text-center text-gray-500">
                                    登録されたイベントはありません
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            events.map((ev) => (
                                <tr key={ev.id} className="border-t">
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {ev.startDate} 〜 {ev.endDate}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {TYPE_LABELS[ev.type] ?? ev.type}
                                    </td>
                                    <td className="px-3 py-2">{ev.label}</td>
                                    {canEdit && (
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => startEdit(ev)}
                                                className="text-blue-600 hover:underline mr-3"
                                            >
                                                編集
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => remove(ev)}
                                                className="text-red-600 hover:underline"
                                            >
                                                削除
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
